import { ReactNode } from "react";
import { ShieldCheck, FileQuestion, Paperclip, Mic, ArrowUp, Check, AlertCircle } from "lucide-react";
import { t } from "@/i18n";

const ScreenBg = ({ pos = "center 40%" }: { pos?: string }) => (
  <div aria-hidden className="absolute inset-0 -z-10" data-testid="mock-screen-bg">
    <div className="absolute inset-0 bg-cover" style={{ backgroundImage: "url(/themes/krusty-krab-bg.webp)", backgroundPosition: pos }} />
    <div className="absolute inset-0 bg-gradient-to-b from-[rgba(224,246,255,0.8)] via-[rgba(240,250,255,0.74)] to-[rgba(255,248,225,0.86)]" />
  </div>
);

export const DesktopFrame = ({ children }: { children: ReactNode }) => (
  <div className="w-full">
    <div className="rounded-[1.4rem] bg-[#1b1b1d] p-2.5 shadow-[0_40px_80px_-30px_rgba(40,30,10,0.45)]">
      <div className="rounded-[0.9rem] overflow-hidden bg-[var(--bg-primary)] aspect-[5/8] sm:aspect-[16/10] relative isolate"><ScreenBg />{children}</div>
    </div>
    <div className="mx-auto w-[22%] h-8 bg-gradient-to-b from-[#d9d6d0] to-[#c8c4bc]" />
    <div className="mx-auto w-[34%] h-2 rounded-full bg-[#cfcbc3]" />
  </div>
);

export const PhoneFrame = ({ children }: { children: ReactNode }) => (
  <div className="rounded-[2.4rem] bg-[#1b1b1d] p-2 shadow-[0_30px_60px_-25px_rgba(40,30,10,0.5)] w-full">
    <div className="rounded-[1.9rem] overflow-hidden bg-[var(--bg-primary)] aspect-[9/19] relative isolate">
      <ScreenBg pos="58% 40%" />
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full bg-[#1b1b1d] z-10" />
      <div className="absolute inset-0 pt-8">{children}</div>
    </div>
  </div>
);

const Out = ({ t }: { t: string }) => <div className="flex justify-end"><p className="bubble-out max-w-[78%] text-[0.7rem] leading-snug">{t}</p></div>;
const In = ({ t, cite, miss }: { t: string; cite?: string; miss?: boolean }) => (
  <div className="flex flex-col items-start">
    <p className={`bubble-in max-w-[82%] text-[0.7rem] leading-snug ${miss ? "!text-[var(--status-error)]" : ""}`}>{t}</p>
    {cite && <span className="text-[0.55rem] faint mt-0.5 px-2 flex items-center gap-1"><ShieldCheck size={9} className="text-[var(--status-ok)]" />{cite}</span>}
  </div>
);

export const MockChat = ({ compact = false }: { compact?: boolean }) => (
  <div className="h-full flex flex-col">
    <div className="text-center py-2 border-b border-[var(--border-color)]"><p className="text-[0.7rem] font-semibold">{t("Chat")}</p></div>
    <div className={`flex-1 flex flex-col gap-2 p-3 ${compact ? "" : "px-6"}`}>
      <Out t={t("How much vanilla syrup goes in the latte?")} />
      <In t={t("15 ml, added to the cup before the espresso.")} cite={t("House Vanilla Latte") + " · v2"} />
      <Out t={t("Can I use oat milk instead?")} />
      <In t={t("No answer found in current knowledge base.")} cite={t("Sent to owner")} miss />
    </div>
    <div className="m-2 flex items-center gap-1.5 rounded-full border border-[var(--border-color)] bg-[var(--bg-surface)] px-2 py-1">
      <Paperclip size={10} className="faint" /><Mic size={10} className="faint" />
      <span className="flex-1 text-[0.6rem] faint">{t("Message")}</span>
      <span className="w-4 h-4 rounded-full bg-[var(--accent-primary)] grid place-items-center"><ArrowUp size={9} color="#fff" /></span>
    </div>
  </div>
);

const Row = ({ tone, title, meta }: { tone: "err" | "ok" | "info"; title: string; meta: string }) => {
  const Icon = tone === "ok" ? Check : tone === "err" ? AlertCircle : FileQuestion;
  const color = tone === "ok" ? "var(--status-ok)" : tone === "err" ? "var(--status-error)" : "var(--accent-primary)";
  return (
    <div className="flex items-center gap-2 py-2 border-b border-[var(--border-color)] last:border-0">
      <span className="w-5 h-5 rounded-full grid place-items-center" style={{ background: `color-mix(in srgb, ${color} 14%, transparent)` }}><Icon size={10} color={color} /></span>
      <span className="flex-1 min-w-0"><span className="block text-[0.65rem] font-semibold truncate">{title}</span><span className="block text-[0.55rem] faint">{meta}</span></span>
    </div>
  );
};

export const MockReview = () => (
  <div className="h-full flex flex-col">
    <div className="text-center py-2 border-b border-[var(--border-color)]"><p className="text-[0.7rem] font-semibold">{t("Review")}</p></div>
    <div className="p-3 flex gap-1.5">
      <span className="chip chip-err !text-[0.55rem] !py-0">{t("{n} need action", { n: 2 })}</span><span className="chip chip-info !text-[0.55rem] !py-0">{t("{n} ready", { n: 1 })}</span><span className="chip chip-ok !text-[0.55rem] !py-0">{t("{n} approved", { n: 6 })}</span>
    </div>
    <div className="px-3">
      <Row tone="err" title={`“${t("Can I use oat milk instead?")}”`} meta={t("Unanswered · today")} />
      <Row tone="err" title={t("Cold brew ratio conflicts")} meta={t("Two SOPs disagree")} />
      <Row tone="info" title={t("Pumpkin spice latte")} meta={t("Draft ready to approve")} />
      <Row tone="ok" title={t("House Vanilla Latte")} meta={t("Approved") + " · v2"} />
    </div>
  </div>
);
